<template>
  <div>
    <!-- Qualified Investor Modal -->
    <Modal
      class="alert-modal"
      title="Investidor qualificado"
      :is-open="qualifiedInvestorModal"
      @close="closeQualifiedModal"
    >
      <p class="wrn-text-left text-size-20 wrn-p-b-1">
        Produto reservado a clientes classificados como
        <b>investidores qualificados</b>
        , sendo seu caso, marque o campo abaixo:
      </p>

      <div
        class="wrn-checkbox-container wrn-c-12 wrn-color-black wrn-input-lg wrn-p-b-1"
      >
        <Checkbox
          v-model="declareQualifiedCheckboxValue"
          class="wrn-bg-white wrn-checkbox"
          :class="{ 'wrn-checked': declareQualifiedCheckboxValue }"
        >
          Sou um investidor qualificado
        </Checkbox>
      </div>

      <p class="wrn-text-left" :style="textStyle">
        Declaro, sob as penas da lei, que possuo investimentos financeiros em
        valor superior a R$ 1.000.000,00 (um milhão de reais) e/ou possuo
        aprovação em exame de qualificação técnica/certificação aprovada pela
        CVM, na forma do art. 9º-B, III, da ICVM 539.
      </p>

      <template #buttons>
        <SimpleButton label="Fechar" @click="closeQualifiedModal" />
        <SimpleButton
          :label="rightButtonLabel"
          :disabled="!declareQualifiedCheckboxValue"
          @click="confirmQualifiedInvestor"
        />
      </template>
    </Modal>

    <!-- Professional Investor Modal -->
    <Modal
      class="alert-modal"
      title="Investidor profissional"
      :is-open="professionalInvestorModal"
      @close="closeProfessionalInvestorModal"
    >
      <p class="wrn-text-lprofessionalInvestorModaleft text-size-20 wrn-p-b-1">
        Produtos destinado a clientes classificados como
        <b>investidores profissionais</b>
        , é necessário entrar em contato com nosso atendimento.
      </p>

      <template #buttons>
        <SimpleButton label="Fechar" @click="closeProfessionalInvestorModal" />
      </template>
    </Modal>

    <!-- Risk Score Modal -->
    <Modal
      class="alert-modal"
      title="Nível de risco"
      :is-open="riskScoreModal"
      @close="closeRiskScoreModal"
    >
      <p class="wrn-text-lprofessionalInvestorModaleft text-size-20 wrn-p-b-1">
        Produto selecionado é incompatível com o nível de risco do seu perfil.
        Deseja adicionar mesmo assim?
      </p>

      <template #buttons>
        <SimpleButton label="Fechar" @click="closeRiskScoreModal" />
        <SimpleButton label="Adicionar" @click="confirmRiskScoreModal" />
      </template>
    </Modal>

    <!-- Product Details Modal -->
    <BaseModal
      :is-open="isOpen"
      size="lg"
      data-testid="product-details-modal"
      v-on="$listeners"
    >
      <template #header>
        <NavBar>
          <span slot="center">Detalhes do produto</span>
          <CloseButton slot="right" @click="$emit('close')" />
        </NavBar>
        <CustomNavBar
          v-if="!isLoading && !isAvailable && detailedInstrument"
          class="wrn-flash-error"
        >
          <span slot="center" class="wrn-color-white">
            Mínimo inicial de
            {{ detailedInstrument.minimumInitialInvestment | money }} para
            adicionar o produto
          </span>
        </CustomNavBar>
      </template>
      <template #content>
        <main class="product-details-modal-content">
          <LoaderIndicator v-if="isLoading" with-container />
          <ProductDescriptionDetailed
            v-else-if="detailedInstrument"
            :detailed-instrument="detailedInstrument"
            :show-product-quotas="showProductQuotas"
          />
        </main>
      </template>

      <template #footer>
        <ModalFooter
          :left-button-label="leftButtonLabel"
          :right-button-label="rightButtonLabel"
          :hide-right-button="hideRightButton"
          @left-button-clicked="$emit('close')"
          @right-button-clicked="rightButtonClick"
        />
      </template>
    </BaseModal>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import { NavBar as CustomNavBar } from '@/modules/common/components/__deprecated__/NavBar';
import { NavBar, BaseModal, LoaderIndicator } from '@warrenbrasil/nebraska-web';
import { CloseButton } from '@/modules/common/components/buttons';
import { SimpleButton } from '@/modules/common/components/__deprecated__/SimpleButton';
import {
  Modal,
  ModalFooter
} from '@/modules/common/components/__deprecated__/Modal';
import { ProductDescriptionDetailed } from '@/modules/products/components/ProductDescriptionDetailed';

import {
  getSingleInstrument,
  checkInstrumentRiskTolerance,
  IDetailedInstrument
} from '@/modules/common/services/http/instrument';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { setUserQualification } from '@/modules/products/services/user-account';
import { moneyViewer } from '@/modules/common/helpers/currency';

import { IInstrument, InvestorType } from '@/types/models/Portfolio';
import { CustomerModule } from '@/modules/common/store/CustomerModule';

@Component({
  components: {
    LoaderIndicator,
    NavBar,
    CustomNavBar,
    BaseModal,
    ModalFooter,
    CloseButton,
    Modal,
    SimpleButton,
    ProductDescriptionDetailed
  },
  filters: {
    money: (value: any) => {
      return moneyViewer(value, true);
    }
  }
})
export default class ProductDetails extends Vue {
  @Prop({ type: Object })
  public instrument!: IInstrument;

  @Prop({ type: Boolean, default: false })
  public isOpen!: boolean;

  @Prop({ type: Boolean })
  public isUnavailableInstrument!: boolean;

  @Prop({ type: String, default: 'FECHAR' })
  public leftButtonLabel!: string;

  @Prop({ type: String, default: 'Ok' })
  public rightButtonLabel!: string;

  @Prop({ type: Boolean, default: false })
  public hideRightButton!: boolean;

  @Prop({ type: Boolean, default: false })
  public ignoreProductRisks!: boolean;

  @Prop({ type: Boolean, default: false })
  public showProductQuotas!: boolean;

  public NebraskaColors = NebraskaColors;
  public isLoading = true;
  public detailedInstrument: IDetailedInstrument | any = null;

  private qualifiedInvestorModal = false;
  private customerIsQualifiedInvestor = false;
  private declareQualifiedCheckboxValue = false;
  private professionalInvestorModal = false;
  private riskScoreModal = false;
  private customerId = '';

  private get customer() {
    return CustomerModule.getCustomer;
  }

  private get isProductQualified() {
    return (
      this.detailedInstrument &&
      this.detailedInstrument.investorType === InvestorType.QUALIFIED
    );
  }

  private get isProductRestrictedToProfessionals() {
    return (
      this.detailedInstrument &&
      this.detailedInstrument.investorType === InvestorType.PROFESSIONAL
    );
  }

  @Watch('isOpen')
  private async getInstrument() {
    if (this.isOpen) {
      this.isLoading = true;

      if (!this.customer) {
        await CustomerModule.fetchCustomer();
      }

      this.detailedInstrument = await getSingleInstrument(
        this.instrument.apiId
      );
      if (!this.ignoreProductRisks)
        this.customerIsQualifiedInvestor =
          this.getCustomerIsQualifiedInvestor();

      this.isLoading = false;
    }
  }

  private getCustomerIsQualifiedInvestor() {
    this.customerId = this.customer!._id;

    return this.customer?.investmentProfile
      ? !!this.customer.investmentProfile.isQualifiedInvestor
      : false;
  }

  private closeQualifiedModal() {
    this.qualifiedInvestorModal = false;
    this.declareQualifiedCheckboxValue = false;
  }

  private closeProfessionalInvestorModal() {
    this.professionalInvestorModal = false;
  }

  private closeRiskScoreModal() {
    this.riskScoreModal = false;
  }

  private confirmRiskScoreModal() {
    this.riskScoreModal = false;
    this.rightButtonClick();
  }

  private get isAvailable() {
    if (!this.detailedInstrument) return false;
    if (this.detailedInstrument.actionStatus === 'BLOCKADD') return false;

    return this.detailedInstrument.isAvailable && !this.isUnavailableInstrument;
  }

  private rightButtonClick() {
    if (this.ignoreProductRisks) return this.rightButtonClicked();

    if (this.isProductQualified && !this.customerIsQualifiedInvestor) {
      this.qualifiedInvestorModal = true;
    } else if (this.isProductRestrictedToProfessionals) {
      this.professionalInvestorModal = true;
    } else if (!this.isCostumerRiskToleranceCompatible()) {
      this.riskScoreModal = true;
    } else {
      this.rightButtonClicked();
    }
  }

  private async isCostumerRiskToleranceCompatible() {
    return (
      await checkInstrumentRiskTolerance(this.customerId, this.instrument.apiId)
    ).isWithinRiskScore;
  }

  private async confirmQualifiedInvestor() {
    await setUserQualification();
    this.closeQualifiedModal();
    this.rightButtonClicked();
  }

  private rightButtonClicked() {
    this.$emit('right-button-clicked', this.instrument);
    this.$emit('close');
  }

  private get textStyle() {
    return { color: NebraskaColors.textSecondary, fontSize: '16px' };
  }
}
</script>

<style lang="less" scoped>
.alert-modal {
  z-index: 4500;
}

.product-details-modal-content {
  padding: @size-spacing-x600 @size-spacing-x400;

  @media @tabletUp {
    padding: @size-spacing-x800 @size-spacing-x1000;
  }
}
</style>
